import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useCompare } from "@/context/CompareContext";
import { magLabel, listingPath } from "@/lib/constants";
import { GitCompareArrows, X, ArrowLeft, BadgeCheck } from "lucide-react";

const BASE_ROWS = [
  { key: "price", label: "Price", get: (l) => (l.listing_type === "buy" ? "ISO" : l.price ? `$${l.price.toLocaleString()}` : "—") },
  { key: "condition", label: "Condition", get: (l) => l.condition },
  { key: "brand", label: "Brand", get: (l) => l.brand },
  { key: "model", label: "Model", get: (l) => l.model },
  { key: "seller", label: "Seller", get: (l) => `@${l.seller_username}${l.seller_is_dealer ? " · Dealer" : ""}` },
  { key: "location", label: "Location", get: (l) => [l.city, l.state].filter(Boolean).join(", ") },
];

const SPEC_ROWS = {
  magnification: { label: "Magnification", get: (l) => magLabel(l) },
  tube_diameter: { label: "Tube Diameter", get: (l) => l.tube_diameter },
  focal_plane: { label: "Focal Plane", get: (l) => (l.focal_plane && l.focal_plane !== "N/A" ? l.focal_plane : "") },
  reticle_type: { label: "Reticle", get: (l) => l.reticle_type },
  mount_type: { label: "Mount", get: (l) => l.mount_type },
  weight_oz: { label: "Weight", get: (l) => (l.weight_oz != null ? `${l.weight_oz} oz` : "") },
};

// Which structured spec rows are relevant per category. Unknown categories get all.
const CATEGORY_SPECS = {
  "Rifle Scopes": ["magnification", "tube_diameter", "focal_plane", "reticle_type", "mount_type", "weight_oz"],
  "Spotting Scopes": ["magnification", "weight_oz"],
  "Red Dots": ["reticle_type", "mount_type", "weight_oz"],
  "Red Dots & Reflex": ["reticle_type", "mount_type", "weight_oz"],
  "Binoculars": ["magnification", "weight_oz"],
  "Rangefinders": ["weight_oz"],
  "Mounts & Rings": ["tube_diameter", "mount_type", "weight_oz"],
  "Night Vision & Thermal": ["magnification", "weight_oz"],
  "Accessories": ["weight_oz"],
};

const ALL_SPEC_KEYS = Object.keys(SPEC_ROWS);
const val = (r, l) => { const v = r.get(l); return v === null || v === undefined ? "" : String(v); };

export default function Compare() {
  const compare = useCompare();
  const navigate = useNavigate();
  const items = compare?.items || [];

  if (items.length < 2) {
    return (
      <div className="min-h-screen text-foreground">
        <Navbar />
        <div className="max-w-[900px] mx-auto px-4 py-20 text-center" data-testid="compare-empty">
          <GitCompareArrows className="w-10 h-10 text-olive-bright mx-auto mb-3" strokeWidth={1.3} />
          <h1 className="font-heading text-3xl uppercase tracking-tight">Compare</h1>
          <p className="text-secondary-foreground font-body mt-2">Pick at least two listings — tick the Compare box on any card in the marketplace.</p>
          <Link to="/feed" className="inline-flex items-center gap-2 mt-5 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase px-5 py-2.5 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Browse listings
          </Link>
        </div>
      </div>
    );
  }

  // Category-aware spec keys: intersection across selected categories (fall back to shared).
  const cats = [...new Set(items.map((i) => i.category))];
  let specKeys;
  if (cats.length === 1) {
    specKeys = CATEGORY_SPECS[cats[0]] || ALL_SPEC_KEYS;
  } else {
    specKeys = ALL_SPEC_KEYS.filter((k) => cats.every((c) => (CATEGORY_SPECS[c] || ALL_SPEC_KEYS).includes(k)));
  }
  const specRows = specKeys.map((k) => ({ key: k, ...SPEC_ROWS[k] }));
  // Hide rows that are empty across ALL selected items.
  const rows = [...BASE_ROWS, ...specRows].filter((r) => items.some((i) => val(r, i) !== "" && val(r, i) !== "—"));
  const isDiff = (r) => new Set(items.map((i) => val(r, i))).size > 1;

  return (
    <div className="min-h-screen text-foreground pb-24">
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-4 py-6" data-testid="compare-page">
        <div className="flex items-center justify-between mb-4">
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2">
            <GitCompareArrows className="w-7 h-7 text-olive-bright" /> Compare
          </h1>
          <button onClick={() => navigate("/feed")} className="font-mono text-xs uppercase text-secondary-foreground hover:text-foreground flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back to marketplace
          </button>
        </div>
        {cats.length > 1 && (
          <p className="text-xs font-mono uppercase text-volt mb-3" data-testid="compare-mixed-note">
            Comparing across categories — showing shared attributes only.
          </p>
        )}

        <div className="overflow-x-auto border border-sniper" data-testid="compare-table-wrap">
          <table className="w-full border-collapse min-w-[560px]">
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-surface-2 border-b border-r border-sniper w-[130px] min-w-[130px]" />
                {items.map((i) => (
                  <th key={i.listing_id} className="bg-surface-2 border-b border-sniper p-3 align-top min-w-[150px]" data-testid={`compare-col-${i.listing_id}`}>
                    <Link to={listingPath(i)} className="block group">
                      <div className="aspect-square bg-obsidian overflow-hidden mb-2">
                        {(i.photos || [])[0] && <img src={i.photos[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />}
                      </div>
                      <span className="font-body text-xs text-foreground line-clamp-2 group-hover:text-volt transition-colors">{i.title}</span>
                    </Link>
                    <button onClick={() => compare.remove(i.listing_id)} data-testid={`compare-remove-${i.listing_id}`}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase text-muted-foreground hover:text-blaze">
                      <X className="w-3 h-3" /> Remove
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const diff = isDiff(r);
                const cellBg = diff ? "bg-olive/10" : "";
                return (
                  <tr key={r.key} data-testid={`compare-row-${r.key}`} data-diff={diff || undefined}>
                    <td className={`sticky left-0 z-10 border-r border-b border-sniper px-3 py-2.5 font-mono text-[10px] uppercase tracking-wide ${diff ? "bg-olive/15 text-olive-bright" : "bg-surface text-muted-foreground"}`}>
                      {r.label}
                    </td>
                    {items.map((i) => (
                      <td key={i.listing_id} className={`border-b border-sniper px-3 py-2.5 text-sm font-body text-center ${cellBg} ${diff ? "text-foreground" : "text-secondary-foreground"}`}>
                        {r.key === "seller" && i.seller_is_dealer ? (
                          <span className="inline-flex items-center gap-1 text-steel">{val(r, i)}<BadgeCheck className="w-3 h-3" /></span>
                        ) : (val(r, i) || "—")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] font-mono uppercase text-muted-foreground mt-2">Rows where the picks differ are highlighted.</p>
      </div>
    </div>
  );
}
